import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Account, AccountsState } from '../types';
import { OnboardingWelcomeStep } from './onboarding/OnboardingWelcomeStep';
import { OnboardingFollowUpStep } from './onboarding/OnboardingFollowUpStep';
import { JoinClassroomPrompt } from './JoinClassroomPrompt';
import { LinkChildPrompt } from './LinkChildPrompt';
import { CreateFirstClassroom } from './CreateFirstClassroom';
import { DjayAvatar, DjayBubble } from '../../mascot';
import type { OnboardingStepState } from '../types';
interface OnboardingWizardProps {
  account: Account;
  accounts: AccountsState;
  onJoinClassroom: (joinCode: string) => {
    success: boolean;
    error?: string;
  };
  onLinkChild: (linkCode: string) => {
    success: boolean;
    error?: string;
  };
  onCreateClassroom: (name: string, grade: number) => void;
  onJoinExistingClassroom?: (classroomId: string, mode: 'transfer' | 'coteach') => void;
  onCompleteOnboarding: (stepState?: OnboardingStepState) => void;
  onNavigateToModule?: (moduleId: string) => void;
  onNavigateToMyClass?: () => void;
}
export function OnboardingWizard({
  account,
  accounts,
  onJoinClassroom,
  onLinkChild,
  onCreateClassroom,
  onJoinExistingClassroom,
  onCompleteOnboarding,
  onNavigateToModule,
  onNavigateToMyClass
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [groupLevel, setGroupLevel] = useState(account.role === 'learner' && account.grade ? account.grade : 6);
  const [stepState, setStepState] = useState<OnboardingStepState>({
    welcome: null,
    action: null,
    followUp: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const handleSkipAction = () => {
    const newState = {
      ...stepState,
      action: 'skipped' as const,
      followUp: 'skipped' as const
    };
    setStepState(newState);
    onCompleteOnboarding(newState); // Skip step 3 if step 2 is skipped
  };
  const handleCompleteFollowUp = () => {
    const newState = {
      ...stepState,
      followUp: 'completed' as const
    };
    setStepState(newState);
    onCompleteOnboarding(newState);
  };
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <OnboardingWelcomeStep key="step0" role={account.role} name={account.name} groupLevel={groupLevel} onNext={() => {
          setStepState({
            ...stepState,
            welcome: 'completed'
          });
          setCurrentStep(1);
        }} onGroupChange={setGroupLevel} data-id="element-344" />;
      case 1:
        return <motion.div key="step1" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="w-full max-w-md mx-auto" data-id="element-345">
            {/* Mascot section for step 1 */}
            <div className="flex flex-col items-center mb-8 relative" data-id="element-346">
              <DjayBubble message={account.role === 'learner' ? 'Heb je een koppelcode van je meester of juf gekregen? Vul hem hier in om in je klas te komen!' : account.role === 'parent' ? 'Vul de koppelcode van je kind in om hun voortgang te kunnen volgen.' : 'Laten we je eerste klas aanmaken.'} variant="welcome" className="mb-4" data-id="element-347" />
              <DjayAvatar emotion={account.role === 'learner' ? 'thinking' : account.role === 'parent' ? 'thinking' : 'celebrating'} size="lg" data-id="element-348" />
            </div>

            {account.role === 'learner' && <div className="space-y-4" data-id="element-349">
                <JoinClassroomPrompt embedded onJoin={code => {
              setIsLoading(true);
              setError(undefined);
              const result = onJoinClassroom(code);
              setIsLoading(false);
              if (result.success) {
                setStepState({
                  ...stepState,
                  action: 'completed'
                });
                setCurrentStep(2);
              } else {
                setError(result.error || 'Code niet gevonden. Probeer opnieuw.');
              }
            }} onSkip={handleSkipAction} isLoading={isLoading} error={error} data-id="element-350" />
                <div className="text-center" data-id="element-351">
                  <button onClick={handleSkipAction} disabled={isLoading} className="w-full py-2 text-sm font-medium text-slate-500 hover:text-orange-600 transition-colors" data-id="element-352">
                    Sla over voor nu
                  </button>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-353">
                    Je kunt later altijd een klas joinen via Instellingen.
                  </p>
                </div>
              </div>}

            {account.role === 'parent' && <div className="space-y-4" data-id="element-354">
                <LinkChildPrompt embedded onLink={code => {
              setIsLoading(true);
              setError(undefined);
              const result = onLinkChild(code);
              setIsLoading(false);
              if (result.success) {
                setStepState({
                  ...stepState,
                  action: 'completed'
                });
                setCurrentStep(2);
              } else {
                setError(result.error || 'Code niet gevonden. Probeer opnieuw.');
              }
            }} onSkip={handleSkipAction} isLoading={isLoading} error={error} data-id="element-355" />
                <div className="text-center" data-id="element-356">
                  <button onClick={handleSkipAction} disabled={isLoading} className="w-full py-2 text-sm font-medium text-slate-500 hover:text-emerald-600 transition-colors" data-id="element-357">
                    Sla over voor nu
                  </button>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-358">
                    U kunt later koppelen via Instellingen.
                  </p>
                </div>
              </div>}

            {account.role === 'teacher' && <div className="space-y-4" data-id="element-359">
                <CreateFirstClassroom embedded accounts={accounts} activeTeacherId={account.id} onCreate={(name, grade) => {
              setIsLoading(true);
              setError(undefined);
              onCreateClassroom(name, grade);
              setIsLoading(false);
              setStepState({
                ...stepState,
                action: 'completed'
              });
              setCurrentStep(2);
            }} onJoinExisting={onJoinExistingClassroom ? (classroomId, mode) => {
              setIsLoading(true);
              setError(undefined);
              onJoinExistingClassroom(classroomId, mode);
              setIsLoading(false);
              setStepState({
                ...stepState,
                action: 'completed'
              });
              setCurrentStep(2);
            } : undefined} onSkip={handleSkipAction} isLoading={isLoading} error={error} data-id="element-360" />
                <div className="text-center" data-id="element-361">
                  <button onClick={handleSkipAction} disabled={isLoading} className="w-full py-2 text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors" data-id="element-362">
                    Sla over voor nu
                  </button>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-363">
                    Zonder klas kunt u geen leerlingen beheren. U kunt later een
                    klas aanmaken via Instellingen.
                  </p>
                </div>
              </div>}
          </motion.div>;
      case 2:
        return <OnboardingFollowUpStep key="step2" role={account.role} groupLevel={groupLevel} onComplete={handleCompleteFollowUp} onNavigateToModule={onNavigateToModule} onNavigateToMyClass={onNavigateToMyClass} data-id="element-364" />;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4" data-id="element-365">
      <AnimatePresence mode="wait" data-id="element-366">{renderStep()}</AnimatePresence>
    </div>;
}